import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import { ICapability } from 'types/api/capabilitiesApi.interface'
import { IPaginatedResponse } from 'types/api/general.interface'
import { baseUrl } from 'utils/axios'
import { getParams, handleErrors } from 'utils/helpers'

interface FinancesInitalState {
  isLoading: boolean
  data: ICapability[]
  totalPages: number
  totalCount: number
  singleCapability: ICapability
}

const initialState: FinancesInitalState = {
  isLoading: false,
  data: [],
  totalCount: 0,
  totalPages: 0,
  singleCapability: null,
}

export const getCapabilities = createAsyncThunk(
  'capabilities/getCapabilities',
  async (params: any, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPaginatedResponse<ICapability>> = await baseUrl.get('capability', {
        params,
      })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSingleCapability = createAsyncThunk(
  'capabilities/getSingleCapability',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<ICapability> = await baseUrl.get('capability/' + id, {})
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addCapability = createAsyncThunk(
  'capabilities/addCapability',
  async (state: ICapability, thunkAPI) => {
    try {
      const resp: AxiosResponse<ICapability> = await baseUrl.post('capability/', state)
      await thunkAPI.dispatch(getCapabilities(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editCapability = createAsyncThunk(
  'capabilities/editCapability',
  async (state: ICapability, thunkAPI) => {
    try {
      const { id } = state
      delete state.id
      const resp: AxiosResponse<ICapability> = await baseUrl.patch('capability/' + id, state)
      await thunkAPI.dispatch(getCapabilities(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteCapability = createAsyncThunk(
  'capabilities/editCapability',
  async (id: number, thunkAPI) => {
    try {
      const resp: AxiosResponse<ICapability> = await baseUrl.delete('capability/' + id)
      await thunkAPI.dispatch(getCapabilities(getParams({ location })))
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const capabilitiesSlice = createSlice({
  name: 'capabilities',
  initialState,
  reducers: {
    clearCapabilities: (state) => {
      state.data = []
      state.totalCount = 0
      state.totalPages = 0
      state.singleCapability = null
    },
  },
  extraReducers: {
    [getCapabilities.pending.type]: (state) => {
      state.isLoading = true
    },
    [getCapabilities.fulfilled.type]: (
      state,
      { payload }: { payload: IPaginatedResponse<ICapability> },
    ) => {
      state.isLoading = false
      state.data = payload.data
      state.totalCount = payload.totalCount
      state.totalPages = payload.totalPages
    },
    [getCapabilities.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSingleCapability.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSingleCapability.fulfilled.type]: (state, { payload }: { payload: ICapability }) => {
      state.singleCapability = payload
      state.isLoading = false
    },
    [getSingleCapability.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addCapability.pending.type]: (state) => {
      state.isLoading = true
    },
    [addCapability.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Capability successfully added')
    },
    [addCapability.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editCapability.pending.type]: (state) => {
      state.isLoading = true
    },
    [editCapability.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Capability successfully updated')
    },
    [editCapability.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteCapability.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteCapability.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Capability successfully deleted')
    },
    [deleteCapability.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const { clearCapabilities } = capabilitiesSlice.actions

export default capabilitiesSlice.reducer
